import { User } from "firebase/auth";
import { apiFetch } from "./client";

export interface Gameweek {
  id: string;
  number: number;
  name: string;
  completedAt: string | null;
  /** True once the preceding gameweek has been completed (i.e. this GW has "kicked off") */
  isStarted: boolean;
}

export interface Competition {
  id: string;
  name: string;
  season: string;
  type: "CLUB" | "INTERNATIONAL";
  status: "UPCOMING" | "ACTIVE" | "COMPLETED";
  clubCount: number;
  supportedModes: string[];
  /** Modes that can currently have a league created on this competition. Computed server-side. */
  creatableModes: string[];
  /** Real gameweeks (number > 0) with isStarted annotations. Always present from GET /competitions. */
  gameweeks: Gameweek[];
  /** Raw per-mode last-joinable-GW cap JSON — resolve per mode with `capForMode`. */
  modeJoinCaps?: unknown;
}

/**
 * Resolves the last-joinable-GW cap for one mode out of the raw `modeJoinCaps` JSON returned by
 * GET /competitions. Mirrors the defensive resolution in the backend predicate
 * (`backend/src/lib/leagueModes.ts`) so a malformed value can only ever hide gameweeks, never
 * expose ones the backend would reject.
 */
export function capForMode(modeJoinCaps: unknown, mode: string): number | null {
  if (modeJoinCaps === null || modeJoinCaps === undefined) return null;
  if (typeof modeJoinCaps !== "object" || Array.isArray(modeJoinCaps)) return 0;
  const raw = (modeJoinCaps as Record<string, unknown>)[mode];
  if (raw === null || raw === undefined) return null;
  return typeof raw === "number" && Number.isInteger(raw) && raw >= 1 ? raw : 0;
}

export function fetchCompetitions(user: User): Promise<Competition[]> {
  return apiFetch(user, "/competitions");
}

export function fetchCompetition(user: User, id: string): Promise<Competition> {
  return apiFetch(user, `/competitions/${id}`);
}
